/**
 * 日落橙模版 - 登录页
 * 
 * 设计核心理念：暖色调渐变背景 + 右侧浮窗卡片
 * 与teal（全屏玻璃态居中卡片）和red（40/60左右分栏品牌区）都不同：
 * - 全屏暖色渐变背景（琥珀→橙→玫瑰）
 * - 右侧浮窗登录卡片（非居中，非品牌区分栏）
 * - 左侧装饰性品牌标语 + 柔和几何图形
 * - 温馨亲切的视觉风格
 */

import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useAuth } from "@/hooks/useAuth";

export default function SunsetLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { siteName: contextSiteName } = useSite();
  const { siteConfig } = useSiteConfig();

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const siteName = contextSiteName || siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.username.trim() || !formData.password.trim()) {
      setError("请填写用户名和密码");
      return;
    }
    setSubmitting(true);
    try {
      await login(formData.username, formData.password);
      navigate("/");
    } catch (err: any) {
      setError(err?.message || "登录失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-[#fffbf5]">
      {/* ── 背景渐变装饰 ── */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-100/50 via-orange-100/40 to-rose-100/40" />
      {/* 柔和几何装饰 */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-amber-400/10 via-orange-400/8 to-rose-400/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-amber-300/10 via-orange-300/8 to-rose-300/5 blur-3xl" />
      <div className="absolute top-1/3 left-1/4 w-72 h-72 rounded-full bg-amber-200/15 blur-2xl" />

      {/* ── 左侧品牌区 ── */}
      <div className="hidden lg:flex w-1/2 relative items-center justify-center p-12">
        <div className="relative z-10 max-w-md text-center">
          {/* Logo */}
          <div className="mb-8 mx-auto">
            {logo ? (
              <img src={logo} alt={siteName} className="h-14 mx-auto" />
            ) : (
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 mx-auto flex items-center justify-center text-white text-3xl font-bold shadow-2xl shadow-amber-500/30">
                {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
              </div>
            )}
          </div>
          
          {/* 浮动装饰卡片 */}
          <div className="relative">
            <div className="absolute -top-8 -right-4 w-24 h-24 rounded-2xl bg-white/60 backdrop-blur-sm shadow-lg rotate-12 border border-amber-100/50 flex items-center justify-center">
              <i className="ri-shield-check-line text-3xl text-amber-500" />
            </div>
            <div className="absolute -bottom-4 -left-2 w-20 h-20 rounded-2xl bg-white/60 backdrop-blur-sm shadow-lg -rotate-6 border border-orange-100/50 flex items-center justify-center">
              <i className="ri-star-line text-2xl text-orange-500" />
            </div>
          </div>

          {siteName ? (
            <>
              <h1 className="text-3xl font-extrabold text-gray-800 mb-4 mt-4">
                欢迎回到<span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent"> {siteName}</span>
              </h1>
              <p className="text-gray-500 leading-relaxed text-sm">
                登录您的账户，管理发布的信息，享受更多会员服务
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-extrabold text-gray-800 mb-4 mt-4">
                欢迎<span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent">回来</span>
              </h1>
              <p className="text-gray-500 leading-relaxed text-sm">
                登录后即可管理您的分类信息
              </p>
            </>
          )}

          {/* 特性展示 */}
          <div className="mt-10 grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-amber-100/50">
              <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center mb-2">
                <i className="ri-file-list-line text-amber-600" />
              </div>
              <div className="text-xs font-semibold text-gray-700">发布管理</div>
              <div className="text-[11px] text-gray-400">添加、编辑您的分类信息</div>
            </div>
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-orange-100/50">
              <div className="w-8 h-8 rounded-xl bg-orange-100 flex items-center justify-center mb-2">
                <i className="ri-vip-crown-line text-orange-600" />
              </div>
              <div className="text-xs font-semibold text-gray-700">会员特权</div>
              <div className="text-[11px] text-gray-400">享受置顶、加V等专属服务</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 右侧登录卡片 ── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 relative z-10">
        <div className="w-full max-w-md">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-amber-500/10 border border-amber-100/50 p-8 sm:p-10">
            {/* 移动端Logo */}
            <div className="lg:hidden mb-6 text-center">
              {logo ? (
                <img src={logo} alt="" className="h-10 mx-auto mb-2" />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 mx-auto flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-amber-500/20">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
                </div>
              )}
              {siteName && <p className="text-sm text-gray-500 mt-2">{siteName}</p>}
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-1">登录账户</h2>
            <p className="text-sm text-gray-400 mb-8">
              还没有账号？{" "}
              <Link to="/register" className="text-amber-600 hover:text-amber-700 font-semibold">
                立即注册
              </Link>
            </p>

            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line" />{error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">用户名 / 手机号</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-user-line text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="请输入用户名"
                    className="w-full pl-11 pr-4 py-3.5 bg-amber-50/50 border border-amber-100 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="请输入密码"
                    className="w-full pl-11 pr-4 py-3.5 bg-amber-50/50 border border-amber-100 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-gray-500 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded accent-amber-500" />
                  记住我
                </label>
                <Link to="/forgot-password" className="text-amber-600 hover:text-amber-700 font-medium">
                  忘记密码？
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-2xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg shadow-amber-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />登录中...
                  </span>
                ) : "立即登录"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-gray-500 inline-flex items-center gap-1">
                <i className="ri-arrow-left-line" />返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
